import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { apiClient, clearAuthToken } from "@/App";
import {
  buildLegacyTimeSlotsFromWeeklyAvailability,
  generateSlotsFromSchedule,
  inferWeeklyAvailabilityFromTimeSlots,
  normalizeWeeklyAvailability,
  SLOT_DURATION_OPTIONS,
  WEEKDAY_LABELS,
} from "@/lib/availability";
import Footer from "@/components/Footer";
import {
  Save,
  Plus,
  Trash2,
  Settings,
  Clock,
  Stethoscope,
  FileText,
  Loader2,
  LogOut,
  LayoutDashboard,
  GripVertical
} from "lucide-react";

const LOGO_URL = "/logo.svg";

const FIELD_TYPES = [
  { value: "text", label: "Text Input" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone Number" },
  { value: "textarea", label: "Text Area" },
  { value: "select", label: "Dropdown Select" },
  { value: "date", label: "Date Picker" },
  { value: "number", label: "Number" }
];

const SettingsPage = ({ setIsAuthenticated }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form state
  const [appointmentTypes, setAppointmentTypes] = useState([]);
  const [newAppointmentType, setNewAppointmentType] = useState("");
  const [weeklyAvailability, setWeeklyAvailability] = useState({});
  const [customFields, setCustomFields] = useState([]);
  const [bookingInstructions, setBookingInstructions] = useState("");
  const [confirmationMessage, setConfirmationMessage] = useState("");

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await apiClient.get("/settings");
      setAppointmentTypes(response.data.appointment_types || []);
      setWeeklyAvailability(response.data.weekly_availability
        ? normalizeWeeklyAvailability(
            response.data.weekly_availability,
            response.data.time_slots || []
          )
        : inferWeeklyAvailabilityFromTimeSlots(response.data.time_slots || [])
      );
      setCustomFields(response.data.custom_fields || []);
      setBookingInstructions(response.data.booking_instructions || "");
      setConfirmationMessage(response.data.confirmation_message || "");
    } catch (error) {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const normalizedAvailability = normalizeWeeklyAvailability(weeklyAvailability);
      await apiClient.patch("/settings", {
        appointment_types: appointmentTypes,
        time_slots: buildLegacyTimeSlotsFromWeeklyAvailability(normalizedAvailability),
        weekly_availability: normalizedAvailability,
        custom_fields: customFields,
        booking_instructions: bookingInstructions,
        confirmation_message: confirmationMessage
      });
      toast.success("Settings saved successfully");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    clearAuthToken();
    setIsAuthenticated(false);
    navigate("/admin");
    toast.success("Logged out successfully");
  };

  // Appointment Types handlers
  const addAppointmentType = () => {
    if (newAppointmentType.trim() && !appointmentTypes.includes(newAppointmentType.trim())) {
      setAppointmentTypes([...appointmentTypes, newAppointmentType.trim()]);
      setNewAppointmentType("");
    }
  };

  const removeAppointmentType = (index) => {
    setAppointmentTypes(appointmentTypes.filter((_, i) => i !== index));
  };

  const updateDayAvailability = (dayKey, updates) => {
    setWeeklyAvailability((current) => ({
      ...current,
      [dayKey]: {
        ...current[dayKey],
        ...updates,
      },
    }));
  };

  // Custom Fields handlers
  const addCustomField = () => {
    const newField = {
      id: `field_${Date.now()}`,
      label: "",
      field_type: "text",
      required: false,
      placeholder: "",
      options: []
    };
    setCustomFields([...customFields, newField]);
  };

  const updateCustomField = (index, updates) => {
    const updated = [...customFields];
    updated[index] = { ...updated[index], ...updates };
    setCustomFields(updated);
  };

  const removeCustomField = (index) => {
    setCustomFields(customFields.filter((_, i) => i !== index));
  };

  const addFieldOption = (fieldIndex) => {
    const updated = [...customFields];
    if (!updated[fieldIndex].options) {
      updated[fieldIndex].options = [];
    }
    updated[fieldIndex].options.push("");
    setCustomFields(updated);
  };

  const updateFieldOption = (fieldIndex, optionIndex, value) => {
    const updated = [...customFields];
    updated[fieldIndex].options[optionIndex] = value;
    setCustomFields(updated);
  };

  const removeFieldOption = (fieldIndex, optionIndex) => {
    const updated = [...customFields];
    updated[fieldIndex].options = updated[fieldIndex].options.filter((_, i) => i !== optionIndex);
    setCustomFields(updated);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <img src={LOGO_URL} alt="Jubail Hospital" className="h-10 w-auto" />
              <div className="hidden sm:block">
                <h1 className="text-lg font-semibold text-gray-900">Settings</h1>
                <p className="text-xs text-gray-500">Manage booking form & options</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/admin/dashboard")}
                className="text-gray-600"
              >
                <LayoutDashboard className="h-4 w-4 mr-1" />
                Dashboard
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                className="text-red-600 border-red-200 hover:bg-red-50"
              >
                <LogOut className="h-4 w-4 mr-1" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full">
        <div className="space-y-6">
          {/* Appointment Types */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Stethoscope className="h-5 w-5 text-teal-600" />
                Appointment Types
              </CardTitle>
              <CardDescription>
                Manage the types of appointments patients can book
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {appointmentTypes.map((type, index) => (
                  <Badge
                    key={index}
                    variant="secondary"
                    className="px-3 py-1.5 text-sm flex items-center gap-2"
                  >
                    {type}
                    <button
                      onClick={() => removeAppointmentType(index)}
                      className="text-gray-500 hover:text-red-500"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  placeholder="Add new appointment type..."
                  value={newAppointmentType}
                  onChange={(e) => setNewAppointmentType(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && addAppointmentType()}
                  className="max-w-xs"
                  data-testid="new-appointment-type-input"
                />
                <Button
                  onClick={addAppointmentType}
                  variant="outline"
                  size="sm"
                  data-testid="add-appointment-type-btn"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Weekly Availability */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-teal-600" />
                Weekly Availability
              </CardTitle>
              <CardDescription>
                Set opening hours and slot duration for each day. Slots are generated automatically.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {Object.entries(WEEKDAY_LABELS).map(([dayKey, label]) => {
                const daySchedule = weeklyAvailability[dayKey] || {
                  enabled: false,
                  start_time: "08:00",
                  end_time: "17:00",
                  slot_duration_minutes: 30,
                };
                const slots = generateSlotsFromSchedule(daySchedule);

                return (
                  <div
                    key={dayKey}
                    className="rounded-xl border border-gray-200 p-4 space-y-4"
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <h3 className="font-medium text-gray-900">{label}</h3>
                        <p className="text-sm text-gray-500">
                          {daySchedule.enabled
                            ? `${slots.length} slot${slots.length === 1 ? "" : "s"} generated`
                            : "Unavailable"}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={daySchedule.enabled}
                          onCheckedChange={(checked) =>
                            updateDayAvailability(dayKey, { enabled: checked })
                          }
                        />
                        <Label className="text-sm">Open for bookings</Label>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-3 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs text-gray-600">Start time</Label>
                        <Input
                          type="time"
                          value={daySchedule.start_time}
                          disabled={!daySchedule.enabled}
                          onChange={(e) =>
                            updateDayAvailability(dayKey, { start_time: e.target.value })
                          }
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-gray-600">End time</Label>
                        <Input
                          type="time"
                          value={daySchedule.end_time}
                          disabled={!daySchedule.enabled}
                          onChange={(e) =>
                            updateDayAvailability(dayKey, { end_time: e.target.value })
                          }
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-gray-600">Slot duration</Label>
                        <Select
                          value={String(daySchedule.slot_duration_minutes)}
                          onValueChange={(value) =>
                            updateDayAvailability(dayKey, {
                              slot_duration_minutes: Number(value),
                            })
                          }
                          disabled={!daySchedule.enabled}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {SLOT_DURATION_OPTIONS.map((minutes) => (
                              <SelectItem key={minutes} value={String(minutes)}>
                                {minutes} minutes
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-xs text-gray-600">Generated slots preview</Label>
                      {slots.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {slots.map((slot) => (
                            <Badge key={`${dayKey}-${slot}`} variant="outline" className="px-2.5 py-1">
                              {slot}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">
                          {daySchedule.enabled
                            ? "Adjust the hours or duration to generate slots for this day."
                            : "This day is currently unavailable."}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {/* Custom Form Fields */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-teal-600" />
                Custom Form Fields
              </CardTitle>
              <CardDescription>
                Add additional fields to the booking form
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {customFields.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">
                  No custom fields added yet. Click "Add Field" to create one.
                </p>
              ) : (
                <div className="space-y-4">
                  {customFields.map((field, index) => (
                    <div
                      key={field.id}
                      className="border rounded-lg p-4 bg-gray-50 space-y-3"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <GripVertical className="h-4 w-4 text-gray-400" />
                          <span className="text-sm font-medium text-gray-700">
                            Field {index + 1}
                          </span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeCustomField(index)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>

                      <div className="grid md:grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Field Label</Label>
                          <Input
                            placeholder="e.g., Insurance Number"
                            value={field.label}
                            onChange={(e) =>
                              updateCustomField(index, { label: e.target.value })
                            }
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Field Type</Label>
                          <Select
                            value={field.field_type}
                            onValueChange={(value) =>
                              updateCustomField(index, { field_type: value })
                            }
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {FIELD_TYPES.map((type) => (
                                <SelectItem key={type.value} value={type.value}>
                                  {type.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Placeholder Text</Label>
                          <Input
                            placeholder="e.g., Enter your insurance number"
                            value={field.placeholder || ""}
                            onChange={(e) =>
                              updateCustomField(index, { placeholder: e.target.value })
                            }
                          />
                        </div>
                        <div className="flex items-center gap-2 pt-5">
                          <Switch
                            checked={field.required}
                            onCheckedChange={(checked) =>
                              updateCustomField(index, { required: checked })
                            }
                          />
                          <Label className="text-sm">Required field</Label>
                        </div>
                      </div>

                      {/* Options for select fields */}
                      {field.field_type === "select" && (
                        <div className="space-y-2 pt-2 border-t">
                          <Label className="text-xs">Dropdown Options</Label>
                          <div className="space-y-2">
                            {(field.options || []).map((option, optIndex) => (
                              <div key={optIndex} className="flex gap-2">
                                <Input
                                  placeholder={`Option ${optIndex + 1}`}
                                  value={option}
                                  onChange={(e) =>
                                    updateFieldOption(index, optIndex, e.target.value)
                                  }
                                  className="flex-1"
                                />
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeFieldOption(index, optIndex)}
                                  className="text-red-500"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            ))}
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => addFieldOption(index)}
                          >
                            <Plus className="h-3 w-3 mr-1" />
                            Add Option
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <Button
                onClick={addCustomField}
                variant="outline"
                className="w-full border-dashed"
                data-testid="add-custom-field-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Custom Field
              </Button>
            </CardContent>
          </Card>

          {/* Messages */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5 text-teal-600" />
                Booking Messages
              </CardTitle>
              <CardDescription>
                Customize messages shown to patients
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Booking Instructions</Label>
                <Textarea
                  placeholder="Instructions shown on the booking form..."
                  value={bookingInstructions}
                  onChange={(e) => setBookingInstructions(e.target.value)}
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label>Confirmation Message</Label>
                <Textarea
                  placeholder="Message shown after successful booking..."
                  value={confirmationMessage}
                  onChange={(e) => setConfirmationMessage(e.target.value)}
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="save-settings-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Settings
                </>
              )}
            </Button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default SettingsPage;
